import InfoIcon from '@mui/icons-material/Info'
import {
  FormControl,
  Stack,
  Box,
  Select,
  MenuItem,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material'
import { useWatch, Controller, useFormContext } from 'react-hook-form'
import { CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import {
  FormatVersion,
  SearchGenre,
  useSearchGenresQuery,
  ReviewSection,
  ReviewTarget,
  CampaignCostHandlingMethod,
  CampaignCategory,
} from '~/generated/graphql'
import { decodeId } from '~/lib/convertId'
import { z } from 'zod'
import { CampaignInputComponent, CampaignInputComponentProps } from '~/features/campaign_input/CampaignInputComponent'
import { colorPalette } from '~/constants/styles/colors'
import { UnsetAlert } from '~/components/common/UnsetAlert'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { categoryField } from './type'

const SearchGenreMapping = [
  '',
  SearchGenre.Gourmet,
  SearchGenre.Travel,
  SearchGenre.Beauty,
  SearchGenre.Leisure,
  SearchGenre.Lifestyle,
  SearchGenre.Apparel,
] as const

export const genreAndSubGenreSchemaFields = {
  searchGenre: z
    .nativeEnum(SearchGenre, {
      required_error: 'ジャンルを選択してください',
    })
    .or(z.literal(''))
    .default('')
    .transform((v, ctx) => {
      if (v === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ジャンルを選択してください',
        })
        return z.NEVER
      }
      return v
    }),
  searchSubGenreIds: z
    .array(z.number())
    .min(1, 'サブジャンルを1つ以上選択してください')
    .max(3, 'サブジャンルは3つまで選択できます'),
  costHandlingMethod: z.nativeEnum(CampaignCostHandlingMethod),
  ...categoryField,
}
export const genreAndSubGenreSchema = z.object(genreAndSubGenreSchemaFields)

export const validateSearchGenre = (data: z.infer<typeof genreAndSubGenreSchema>, ctx: z.RefinementCtx) => {
  if (data.searchGenre === SearchGenre.Travel && data.costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign)
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'インフルエンサー立替型が選択されているためトラベルジャンルは選択できません',
      path: ['searchGenre'],
    })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const partialSchema = genreAndSubGenreSchema.partial()

type Props = CampaignInputComponentProps & { isCpEditFormPage?: boolean }

export const GenreAndSubGenre = ({ description, isCpEditFormPage = false }: Props) => {
  const { control, watch, setValue, getValues } = useFormContext<z.input<typeof partialSchema>>()
  const searchGenre = watch('searchGenre')
  const searchSubGenreIds = watch('searchSubGenreIds')
  const costHandlingMethod = getValues('costHandlingMethod')
  const category = getValues('category')
  const notSelectableTravelGenre =
    costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign && category === CampaignCategory.Service

  const maxSubGenreSelection = 3 // サブジャンル選択可能数

  const { data: searchGenres } = useSearchGenresQuery()
  const searchSubGenresData =
    searchGenres?.searchGenres.find((e) => SearchGenreMapping[decodeId(e.id).id] === searchGenre)?.subGenres ?? []

  const isSubGenreSelectable = (subGenreId: number, field: ReturnType<typeof useWatch>): boolean => {
    return field.value.length < maxSubGenreSelection || field.value.includes(subGenreId)
  }

  const setSubGenreSelection = (subGenreId: number, checked: boolean) => {
    const newSubGenres = checked
      ? [...(searchSubGenreIds || []), subGenreId]
      : searchSubGenreIds?.filter((id) => id !== subGenreId)
    setValue('searchSubGenreIds', newSubGenres, { shouldValidate: true, shouldDirty: true })
  }

  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.selectServiceGenre}
      required={true}
      description={description}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.SearchSubGenre],
      }}
    >
      <>
        <Stack>
          <Typography variant="subtitle2" color="text.secondary">
            ジャンル
          </Typography>
          <Controller
            name="searchGenre"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error?.message}>
                <Select
                  id="search-genre"
                  labelId="search-genre-label"
                  displayEmpty
                  {...field}
                  fullWidth
                  onChange={(e) => {
                    const newValue = e.target.value
                    // 違うジャンルが選ばれた場合にリセット
                    if (newValue !== field.value) {
                      setValue('searchSubGenreIds', [])
                    }
                    field.onChange(newValue)
                  }}
                  disabled={isCpEditFormPage}
                  inputRef={ref}
                  error={!!error}
                >
                  <MenuItem disabled value="">
                    一つ選択してください
                  </MenuItem>
                  {searchGenres?.searchGenres.map((e) => {
                    const isDisabled =
                      SearchGenreMapping[decodeId(e.id).id] === SearchGenre.Travel && notSelectableTravelGenre
                    return (
                      <MenuItem
                        key={decodeId(e.id).id}
                        value={SearchGenreMapping[decodeId(e.id).id]}
                        disabled={isDisabled}
                      >
                        <Stack>
                          {e.name}
                          {isDisabled && (
                            <FormHelperText sx={{ color: colorPalette.text.secondary, textWrap: 'wrap' }}>
                              ※インフルエンサー立替型が選択されているためトラベルジャンルは選択できません
                            </FormHelperText>
                          )}
                        </Stack>
                      </MenuItem>
                    )
                  })}
                </Select>
                <ValidationErrorMessage error={error?.message} />
              </FormControl>
            )}
          />
          {isCpEditFormPage && (
            <FormHelperText margin="dense" sx={{ color: colorPalette.text.secondary, mt: 0.5, ml: 0 }}>
              ※ジャンルは編集できません
            </FormHelperText>
          )}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            サブジャンル (1-3個まで選択してください)
          </Typography>
          {searchGenre !== '' ? (
            <FormControl component="fieldset">
              <Controller
                name="searchSubGenreIds"
                control={control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <FormGroup row>
                    {searchSubGenresData.map((subGenre, index) => {
                      const subGenreId = decodeId(subGenre.id).id
                      return (
                        <FormControlLabel
                          key={subGenreId}
                          control={
                            <Checkbox
                              checked={field.value?.includes(subGenreId)}
                              onChange={(e) => setSubGenreSelection(subGenreId, e.target.checked)}
                              value={subGenreId}
                              disabled={!isSubGenreSelectable(subGenreId, field) && notSelectableTravelGenre}
                              inputRef={index === 0 ? ref : undefined}
                            />
                          }
                          label={subGenre.name}
                          sx={{ mr: 3 }}
                        />
                      )
                    })}
                    <ValidationErrorMessage sx={{ mt: 1 }} error={error?.message} />
                  </FormGroup>
                )}
              />
            </FormControl>
          ) : (
            <UnsetAlert icon={<InfoIcon color="action" />} sx={{ mt: 1 }}>
              ジャンルを選択すると、関連するサブジャンルが表示されます
            </UnsetAlert>
          )}
        </Box>
      </>
    </CampaignInputComponent>
  )
}
